import { z } from 'zod';
import { IsoDateCodec } from './date-codecs.js';
import { UuidSchema } from './primitives/uuid.js';
// ============================================================================
// Admin Follows Schemas
// ============================================================================

// Common query filters
export const AdminFollowsQuerySchema = z.object({
  userId: UuidSchema.optional(),
  observerProfileId: z.string().optional(),
});

export type AdminFollowsQuery = z.infer<typeof AdminFollowsQuerySchema>;

// Health
export const AdminFollowsHealthResponseSchema = z.object({
  orphans: z.object({
    count: z.number().int(),
    samples: z.array(
      z.object({
        userId: z.string(),
        observerProfileId: z.string(),
        subjectProfileId: z.string(),
      })
    ),
  }),
  snapshots: z.object({
    incompleteCount: z.number().int(),
    lastFollowers: z.date().nullable(),
    lastFollowing: z.date().nullable(),
  }),
  filters: z.object({
    userId: z.string().nullable(),
    observerProfileId: z.string().nullable(),
  }),
  timestamp: z.date(),
});

export type AdminFollowsHealthResponse = z.infer<typeof AdminFollowsHealthResponseSchema>;

// Summary
export const AdminFollowsSummaryResponseSchema = z.object({
  counts: z.object({
    followers: z.number().int(),
    following: z.number().int(),
    mutuals: z.number().int(),
  }),
  lastSnapshots: z.object({
    followers: z.date().nullable(),
    following: z.date().nullable(),
  }),
  delta24h: z.object({
    started: z.number().int(),
    ended: z.number().int(),
  }),
  filters: z.object({
    userId: z.string().nullable(),
    observerProfileId: z.string().nullable(),
  }),
  timestamp: z.date(),
});

export type AdminFollowsSummaryResponse = z.infer<typeof AdminFollowsSummaryResponseSchema>;

// Refresh Recent
export const AdminFollowsRefreshRecentBodySchema = z.object({
  limit: z.number().int().min(10).max(1000).default(200),
  days: z.number().int().min(1).max(180).default(30),
});

export type AdminFollowsRefreshRecentBody = z.infer<typeof AdminFollowsRefreshRecentBodySchema>;

export const AdminFollowsRefreshRecentResponseSchema = z.object({
  refreshed: z.number().int(),
  profilesUpserted: z.number().int(),
  relationshipsUpserted: z.number().int(),
  requested: z.object({
    limit: z.number().int(),
    days: z.number().int(),
  }),
  note: z.string().optional(),
});

export type AdminFollowsRefreshRecentResponse = z.infer<typeof AdminFollowsRefreshRecentResponseSchema>;

export const AdminFollowsRefreshRecentInfoResponseSchema = z.object({
  message: z.string(),
});

export type AdminFollowsRefreshRecentInfoResponse = z.infer<typeof AdminFollowsRefreshRecentInfoResponseSchema>;

// Sync Followers/Following
export const AdminFollowsSyncResponseSchema = z.object({
  success: z.boolean(),
  action: z.string(),
  userId: z.string(),
  note: z.string().optional(),
});

export type AdminFollowsSyncResponse = z.infer<typeof AdminFollowsSyncResponseSchema>;

export const AdminFollowsSyncInfoResponseSchema = z.object({
  message: z.string(),
  action: z.string(),
  userId: z.string(),
});

export type AdminFollowsSyncInfoResponse = z.infer<typeof AdminFollowsSyncInfoResponseSchema>;

export const AdminFollowsSyncRequestSchema = z.undefined();
export type AdminFollowsSyncRequest = z.infer<typeof AdminFollowsSyncRequestSchema>;

export const AdminFollowsSyncConflictResponseSchema = z.object({
  success: z.boolean(),
  action: z.string(),
  userId: z.string(),
  note: z.string(),
});
export type AdminFollowsSyncConflictResponse = z.infer<typeof AdminFollowsSyncConflictResponseSchema>;

// Churn
export const AdminFollowsChurnQuerySchema = z.object({
  userId: UuidSchema.optional(),
  observerProfileId: z.string().optional(),
  window: z.enum(['7d', '30d']).default('7d'),
});

export type AdminFollowsChurnQuery = z.infer<typeof AdminFollowsChurnQuerySchema>;

export const AdminFollowsChurnResponseSchema = z.object({
  window: z.string(),
  since: z.date(),
  series: z.array(
    z.object({
      date: z.string(),
      started: z.number().int(),
      ended: z.number().int(),
    })
  ),
  timestamp: z.date(),
});

export type AdminFollowsChurnResponse = z.infer<typeof AdminFollowsChurnResponseSchema>;

// Events
export const AdminFollowsEventsQuerySchema = z.object({
  userId: UuidSchema.optional(),
  observerProfileId: z.string().optional(),
  since: IsoDateCodec.optional(),
  limit: z.number().int().min(1).max(500).default(100),
});

export type AdminFollowsEventsQuery = z.infer<typeof AdminFollowsEventsQuerySchema>;

export const AdminFollowsEventsResponseSchema = z.object({
  events: z.array(
    z.object({
      id: z.string(),
      userId: z.string(),
      observerProfileId: z.string(),
      subjectProfileId: z.string(),
      direction: z.string(),
      action: z.string(),
      eventAt: z.date(),
      subjectUsername: z.string().nullable(),
      subjectName: z.string().nullable(),
    })
  ),
  count: z.number().int(),
  timestamp: z.date(),
});

export type AdminFollowsEventsResponse = z.infer<typeof AdminFollowsEventsResponseSchema>;
